import { useEffect, useRef } from 'react'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useLanguage } from '@context'
import { useGetNearbyVisibility, useShareMyLocation, useUpdateNearbyVisibility } from '@hooks'
import { Coords, Scope, showSuccessToast } from '@utils'

// The visibility chosen before hiding from the map (e.g. "Pro" only), so that
// showing myself again restores it instead of widening it to everyone.
const VISIBILITY_BEFORE_HIDING_KEY = 'map_visibility_before_hiding'

/**
 * My visibility on my contacts' maps, switched by the map's eye: the same
 * setting as "Contacts à proximité" (HIDDEN = not visible to anyone). It also
 * decides whether my live position is shared (saved on my profile, at most
 * once a day).
 */
export function useMyMapVisibility(
  accountId: string | undefined,
  livePosition: Coords | null,
  // How to confirm the change; the app-wide toast by default.
  {
    onConfirm = (message: string) => showSuccessToast(message),
  }: {
    onConfirm?: (message: string) => void
  } = {},
) {
  const { t } = useLanguage()
  const { data: visibilityData, isLoading, isError } = useGetNearbyVisibility()
  const { mutate: updateVisibility, isPending } = useUpdateNearbyVisibility()
  const shareMyLocation = useShareMyLocation()
  const isHidden = visibilityData?.scope === 'HIDDEN'
  const memoryKey = `${VISIBILITY_BEFORE_HIDING_KEY}_${accountId ?? ''}`

  const toggle = async () => {
    if (isHidden) {
      const before = await AsyncStorage.getItem(memoryKey).catch(() => null)
      const restored =
        before === Scope.PROFESSIONAL || before === Scope.PRIVATE ? before : undefined
      // Say who sees me again: only that group when "Pro" / "Privé" is back.
      const message =
        restored === Scope.PROFESSIONAL
          ? t('mapScreen.nowVisiblePro')
          : restored === Scope.PRIVATE
            ? t('mapScreen.nowVisiblePrivate')
            : t('mapScreen.nowVisible')
      updateVisibility({ scope: restored }, { onSuccess: () => onConfirm(message) })
    } else {
      await AsyncStorage.setItem(memoryKey, visibilityData?.scope ?? '').catch(() => {})
      updateVisibility(
        { scope: 'HIDDEN' },
        { onSuccess: () => onConfirm(t('mapScreen.nowHidden')) },
      )
    }
  }

  // Share my live position with my contacts' maps (at most once a day, see
  // useShareMyLocation), once my visibility is known, and never when I chose
  // to be invisible. A failed read leaves it unknown: nothing is shared, and
  // a later successful read still can.
  const hasHandledLivePosition = useRef(false)
  useEffect(() => {
    if (!livePosition || !accountId || isLoading || isError || hasHandledLivePosition.current) return
    hasHandledLivePosition.current = true
    if (isHidden) return
    void shareMyLocation(accountId, livePosition)
  }, [livePosition, accountId, isLoading, isError, isHidden, shareMyLocation])

  return { isHidden, isReady: !isLoading, isPending, toggle }
}
